
import Vue, { PropType } from 'vue';
import { functions } from '@/firebase';
import { mapActions, mapState, mapGetters } from 'vuex';
import DialogContent from '@/components/base/DialogContent.vue';
import DialogOrBottomSheet from '@/components/base/DialogOrBottomSheet.vue';
import { SNACKBAR_STATUS } from '@/constants/constants';
import { Team, TeamUserRole, TeamUserRoleDef } from '@run-diffusion/shared';
import TeamMemberTeamRoleTableCell from '@/views/Team/TeamDashboard/TeamMemberTeamRoleTableCell.vue';
import ActionsIsland from '@/components/ActionsIsland.vue';
import EmptyState from '@/components/states/EmptyState.vue';
import { db } from '@/firebase';
import { get$bindFirestoreOptions } from '@/mixins/VuexFirestoreBindingsMixin';
import pluralize from 'pluralize';

interface ProcessedTeam {
	id: string
	name: string
	companyId: string
	currentRoleDef: TeamUserRoleDef | null
	currentRoleLabel: string
	currentRoleDefId: string | null
	updateData?: {
		loading: boolean
		success?: boolean
	}
}

export default Vue.extend({
	name: 'EditTeamMemberTeams',
	props: {
		value: { type: Boolean, default: false },
		currTeams: { type: Array as PropType<Team[]>, default: () => [] },
		teamMember: { type: Object, default: null },
	},
	data () {
		const DEFAULT_PAGINATION = {
			sortBy: ['name'],
			sortDesc: [false],
		};
		return {
			saving: false,
			initing: false,

			// FORM
			formValid: true,

			// Global toggles
			sendEmailToggle: true,

			// New Roles
			availableRoleDefs: [] as TeamUserRoleDef[],
			loadingAvailableRoleDefs: false,
			selectedRoleDefId: null as string | null,

			// Processed teams data
			processedTeams: [] as ProcessedTeam[],

			// Table config
			itemsPerPageOptions: [25, 100],
			DEFAULT_PAGINATION,
			pagination: DEFAULT_PAGINATION,
		};
	},
	watch: {
		async value (newVal: boolean) {
			if (newVal) await this.init();
		},
	},
	computed: {
		...mapState([
			'team',
			'user',
			'authUser',
		]),
		...mapGetters([
			'displayLabels',
			'companyClubOfferBenefits',
		]),
		headers () {
			return [
				{
					text: `${this.displayLabels.team} name`,
					value: 'name',
					align: 'left',
					class: 'py-0 pl-4 pr-1',
					sortable: true,
				},
				{
					text: `Current ${this.displayLabels.team} Role`,
					value: 'currentRoleLabel',
					align: 'left',
					class: 'py-0 px-1',
					sortable: false,
					noDownload: true,
				},
				{
					text: '',
					value: 'processing',
					align: 'left',
					class: 'py-0 px-1',
					sortable: false,
					noDownload: true,
				},
			];
		},
		newRoleDef (): TeamUserRoleDef | null {
			return (this.availableRoleDefs || []).find((r: TeamUserRoleDef) => r.id === this.selectedRoleDefId) || null;
		},
	},
	methods: {
		...mapActions([
			'updateSnackbar',
			'setUpsellDialog',
		]),
		onTeamRolesUpsell () {
			this.setUpsellDialog({ noTeamRoles: true });
		},
		async init () {
			this.initing = true;
			try {
				this.selectedRoleDefId = null;
				this.sendEmailToggle = true;
				await this.processTeams();
				await this.bindTeamUserRoleDefs();
			} catch (e) {
				// non-fatal
			} finally {
				this.initing = false;
			}
		},
		async processTeams () {
			if (!this.currTeams || !this.currTeams.length) {
				this.processedTeams = [];
				return;
			}

			const nextTeams: ProcessedTeam[] = [];

			for (const team of this.currTeams) {
				const processedTeam: ProcessedTeam = {
					id: team.id || '',
					name: team.name || '',
					companyId: team.companyId || '',
					currentRoleDef: null,
					currentRoleLabel: 'None',
					currentRoleDefId: null,
				};

				await this.fetchTeamCurrentRole(processedTeam);
				nextTeams.push(processedTeam);
			}

			this.processedTeams = nextTeams;
		},
		async fetchTeamCurrentRole (team: ProcessedTeam) {
			try {
				if (!team.id || !this.teamMember?.userId) return;

				const roleId: string = `${team.id}_${this.teamMember.userId}`;
				const snap = await db.doc(`teamUserRoles/${roleId}`).get();
				const data: TeamUserRole = snap.data() as TeamUserRole;
				if (data && data.roleDefId) {
					team.currentRoleDefId = data.roleDefId;
					const roleDef: TeamUserRoleDef = this.availableRoleDefs.find(r => r.id === data.roleDefId);
					if (roleDef) {
						team.currentRoleDef = roleDef;
						team.currentRoleLabel = roleDef.name;
					}
				}
			} catch (e) {
				// non-fatal
				console.error('Error fetching current team user role for team:', team.name, e);
			}
		},
		async bindTeamUserRoleDefs () {
			this.loadingAvailableRoleDefs = true;
			try {
				// Use the first team's companyId, or fallback to current team's companyId
				const companyId: string = this.processedTeams[0]?.companyId || this.team?.companyId;
				if (!companyId) return;

				// index created: teamUserRoleDefs: companyId Ascending isDeleted Ascending level Descending __name__ Descending
				const roleDefsRef = db
					.collection('teamUserRoleDefs')
					.where('companyId', '==', companyId)
					.where('isDeleted', '==', false)
					.orderBy('level', 'desc');

				await this.$bind(
					'availableRoleDefs',
					roleDefsRef,
					get$bindFirestoreOptions(),
				);

				// Update processed teams with role definitions
				if (this.availableRoleDefs && this.availableRoleDefs.length) {
					this.processedTeams.forEach(team => {
						if (team.currentRoleDefId) {
							const roleDef = this.availableRoleDefs.find(r => r.id === team.currentRoleDefId);
							if (roleDef) {
								team.currentRoleDef = roleDef;
								team.currentRoleLabel = roleDef.name;
							}
						}
					});

					// Set a default selection if none chosen yet
					if (!this.selectedRoleDefId) {
						this.selectedRoleDefId = this.availableRoleDefs[0].id;
					}
				}
			} catch (e) {
				console.error('Error binding team user role defs:', e);
			} finally {
				this.loadingAvailableRoleDefs = false;
			}
		},
		onResetAndClose () {
			this.$emit('input', false);
		},
		onDialogInput (value: boolean) {
			if (!value) {
				this.onResetAndClose();
			}
		},
		async onSave () {
			if (!this.currTeams || !this.currTeams.length) return;
			if (!this.selectedRoleDefId) {
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: 'Please select a role',
					show: true,
				});
				return;
			}

			if (!this.$refs.form.validate()) {
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: 'Form invalid, check fields',
					show: true,
				});
				return;
			}

			this.saving = true;

			// Initialize per-row update state to loading
			this.processedTeams.forEach((team: any) => {
				this.$set(team, 'updateData', { loading: true });
			});

			const onError: Function = (e: any) => {
				console.error('Error updating Team Roles: ', e);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: 'Error updating team roles',
					show: true,
				});
			};

			try {
				const updatePromises = this.processedTeams.map(async (team: any) => {
					const functionRef = functions.httpsCallable('manageTeamUserRole');
					try {
						const { success } = (await functionRef({
							teamId: team.id,
							email: this.teamMember.email,
							teamUserRoleDefId: this.selectedRoleDefId,
							sendEmailNotification: this.sendEmailToggle,
							updateAllTeams: false, // Always false since we're updating specific teams
						})).data;
						this.$set(team, 'updateData', { loading: false, success: !!success });
						return { success: !!success, teamName: team.name };
					} catch (err) {
						console.error('Error updating team', team?.name, err);
						this.$set(team, 'updateData', { loading: false, success: false });
						return { success: false, teamName: team.name };
					}
				});

				const results: any[] = await Promise.all(updatePromises);
				const failedUpdates = results.filter(r => !r.success);

				if (failedUpdates.length > 0) {
					onError(new Error(`Failed to update ${failedUpdates.length} teams`));
				} else {
					this.updateSnackbar({
						status: SNACKBAR_STATUS.SUCCESS,
						message: `Success! Updated ${this.processedTeams.length} team ${pluralize('role', this.processedTeams.length)}`,
						show: true,
					});
					this.$emit('success');
					this.onResetAndClose();
				}
			} catch (e) {
				console.error(e);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: 'Error updating team roles, please try again',
					show: true,
				});
			} finally {
				this.saving = false;
			}
		},
	},
	components: {
		DialogContent,
		DialogOrBottomSheet,
		ActionsIsland,
		EmptyState,
		TeamMemberTeamRoleTableCell,
	},
});
